// src/utils/shareUtils.js - Enhanced comprehensive sharing utilities with WeChat link support
import QRCode from 'qrcode';

/**
 * Generate share message for an event
 */
export const generateShareMessage = (event, venue = null, registrationCount = 0, includeLink = false) => {
  const eventDate = new Date(event.startDate);
  const eventEndDate = new Date(event.endDate);
  
  // Format date
  const dateStr = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Format time
  const startTime = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  const endTime = eventEndDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  // Location info
  const location = venue ? 
    `${venue.name}${venue.address ? `, ${venue.address}` : ''}` :
    `${event.venueName || 'TBD'}`;
  
  // Registration status
  const maxParticipants = event.maxParticipants || 0;
  const spotsLeft = Math.max(0, maxParticipants - registrationCount);
  const registrationInfo = maxParticipants > 0
    ? (spotsLeft > 0
        ? `${registrationCount}/${maxParticipants} registered • ${spotsLeft} spots left`
        : `Event Full (${registrationCount}/${maxParticipants})`)
    : null;
  
  // Price info
  const priceInfo = event.price > 0 ? `$${event.price} per person` : 'Free';
  
  const baseMessage = `${event.title}

📅 ${dateStr}
⏰ ${startTime} - ${endTime}
📍 ${location}
💰 ${priceInfo}${registrationInfo ? `
👥 ${registrationInfo}` : ''}
`;

  if (includeLink) {
    const eventLink = getEventUrl(event.id);
    return `${baseMessage}\n Register: ${eventLink}`;
  }

  return baseMessage;
};

/**
 * Generate detailed share message with all event information
 */
export const generateDetailedShareMessage = (event, venue = null, registrationCount = 0, includeLink = true) => {
  const eventDate = new Date(event.startDate);
  const eventEndDate = new Date(event.endDate);
  
  // Format date
  const dateStr = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Format time
  const startTime = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  const endTime = eventEndDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  // Location info
  const location = venue ? venue.name : event.venueName || 'TBD';
  const address = venue && venue.address ? venue.address : '';
  
  // Registration status
  const maxParticipants = event.maxParticipants || 0;
  const spotsLeft = Math.max(0, maxParticipants - registrationCount);
  const registrationInfo = maxParticipants > 0
    ? (spotsLeft > 0
        ? `${registrationCount}/${maxParticipants} registered, ${spotsLeft} spots left`
        : `Event Full (${registrationCount}/${maxParticipants})`)
    : null;
  
  const messageWithDetails = `🏸 ${event.title}

📅 Date: ${dateStr}

⏰ Time: ${startTime} - ${endTime}

📍 Venue: ${location}
${address ? `📍 Address: ${address}` : ''}

💰 Price: ${event.price > 0 ? `$${event.price} per person` : 'Free event'}
${registrationInfo ? `\n👥 Registration: ${registrationInfo}\n` : ''}
${event.description ? `📝 Description: ${event.description}` : ''}

Come join us for some badminton fun! 🏸✨`;

  if (includeLink) {
    const eventLink = getEventUrl(event.id);
    return `${messageWithDetails}\n\n🔗 Register or view details: ${eventLink}`;
  }

  return messageWithDetails;
};

/**
 * Generate short message with essential info
 */
export const generateShortShareMessage = (event, venue = null, registrationCount = 0, includeLink = true) => {
  const eventDate = new Date(event.startDate);
  
  const dateStr = eventDate.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  
  const startTime = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  const location = venue ? venue.name : event.venueName || 'TBD';

  const maxParticipants = event.maxParticipants || 0;
  const spotsLeft = Math.max(0, maxParticipants - registrationCount);
  const registrationLine = maxParticipants > 0
    ? (spotsLeft > 0 ? `👥 ${spotsLeft} spots left` : `👥 Full`)
    : '';
  
  const shortMessage = `🏸 ${event.title}

📅 ${dateStr} at ${startTime}
📍 ${location}
💰 ${event.price > 0 ? `$${event.price}` : 'Free'}${registrationLine ? `\n${registrationLine}` : ''}`;

  if (includeLink) {
    const eventLink = getEventUrl(event.id);
    return `${shortMessage}\n\n🔗 ${eventLink}`;
  }

  return shortMessage;
};

/**
 * Get the event URL for sharing
 */
export const getEventUrl = (eventId) => {
  return `${window.location.origin}/event/${eventId}`;
};

/**
 * Generate QR Code for event
 */
export const generateEventQRCode = async (eventId, options = {}) => {
  const url = getEventUrl(eventId);
  
  const qrOptions = {
    width: 256,
    margin: 2,
    color: {
      dark: '#000000',
      light: '#FFFFFF'
    },
    errorCorrectionLevel: 'M',
    ...options
  };
  
  try {
    const qrCodeDataURL = await QRCode.toDataURL(url, qrOptions);
    return qrCodeDataURL;
  } catch (error) {
    console.error('Error generating QR code:', error);
    throw new Error('Failed to generate QR code');
  }
};

/**
 * Share to WhatsApp
 */
export const shareToWhatsApp = (event, venue = null, registrationCount = 0) => {
  const message = generateShareMessage(event, venue, registrationCount, true);
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank');
};

/**
 * Share to Facebook
 */
export const shareToFacebook = (event, venue = null, registrationCount = 0) => {
  const url = getEventUrl(event.id);
  const title = `${event.title} - Badminton Event`;
  const description = generateShareMessage(event, venue, registrationCount, false);
  
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(`${title}\n\n${description}`)}`;
  window.open(facebookUrl, '_blank', 'width=600,height=400');
};

/**
 * Enhanced Share to WeChat with link and detailed message options
 */
export const shareToWeChat = async (event, venue = null, registrationCount = 0) => {
  try {
    // Generate the direct event link
    const eventLink = getEventUrl(event.id);
    
    // Create detailed message with all event information
    const messageWithDetails = generateDetailedShareMessage(event, venue, registrationCount, true);
    
    // Create short message version
    const shortMessage = generateShortShareMessage(event, venue, registrationCount, true);
    
    // Generate QR code for the event link (optional)
    let qrCode = null;
    try {
      qrCode = await generateEventQRCode(event.id);
    } catch (error) {
      console.warn('Could not generate QR code for WeChat share:', error);
    }

    return {
      eventLink,
      messageWithDetails,
      shortMessage,
      qrCode,
      instruction: "Copy the message and link to share in WeChat",
      platform: 'wechat'
    };
  } catch (error) {
    console.error('Error preparing WeChat share:', error);
    throw error;
  }
};

/**
 * Copy event details to clipboard
 */
export const copyEventToClipboard = (event, venue = null, registrationCount = 0) => {
  const message = generateShareMessage(event, venue, registrationCount, true);
  return navigator.clipboard.writeText(message);
};

/**
 * Native Web Share API (if supported)
 */
export const shareViaWebShare = async (event, venue = null, registrationCount = 0) => {
  if (!navigator.share) {
    throw new Error('Web Share API not supported');
  }
  
  const title = `${event.title} - Badminton Event`;
  const text = generateShareMessage(event, venue, registrationCount, false);
  const url = getEventUrl(event.id);
  
  try {
    await navigator.share({
      title,
      text,
      url
    });
  } catch (error) {
    if (error.name !== 'AbortError') {
      throw error;
    }
  }
};

/**
 * Download QR code as image
 */
export const downloadQRCode = async (eventId, filename = null) => {
  try {
    const qrCodeDataURL = await generateEventQRCode(eventId);
    
    // Create download link
    const link = document.createElement('a');
    link.download = filename || `badminton-event-${eventId}-qr.png`;
    link.href = qrCodeDataURL;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading QR code:', error);
    throw new Error('Failed to download QR code');
  }
};

/**
 * Check if Web Share API is supported
 */
export const isWebShareSupported = () => {
  return navigator.share !== undefined;
};

/**
 * Detect the WeChat in-app browser
 */
export const isWeChatBrowser = () => {
  return typeof navigator !== 'undefined' && /micromessenger/i.test(navigator.userAgent);
};

/**
 * Get available share methods based on device/browser
 */
export const getAvailableShareMethods = () => {
  const methods = [
    { 
      id: 'copy', 
      name: 'Copy Details & Link', 
      icon: 'copy',
      description: 'Copy event details with registration link'
    },
    
    { 
      id: 'qr', 
      name: 'QR Code', 
      icon: 'qr',
      description: 'Generate QR code for easy scanning'
    }
  ];
  
  if (isWebShareSupported()) {
    methods.unshift({ 
      id: 'native', 
      name: 'Share', 
      icon: 'share',
      description: 'Use device\'s native sharing'
    });
  }

  // Inside the WeChat in-app browser, surface a WeChat option first.
  if (isWeChatBrowser()) {
    methods.unshift({
      id: 'wechat',
      name: 'Share in WeChat',
      icon: 'wechat',
      description: 'Send to friends or Moments'
    });
  }
  
  return methods;
};

/**
 * Generate share message in Chinese
 */
export const generateShareMessageZh = (event, venue = null, registrationCount = 0, includeLink = false) => {
  const eventDate = new Date(event.startDate);
  const eventEndDate = new Date(event.endDate);

  const dateStr = `${eventDate.getMonth() + 1}月${eventDate.getDate()}日 ${['周日','周一','周二','周三','周四','周五','周六'][eventDate.getDay()]}`;

  const pad = (n) => String(n).padStart(2, '0');
  const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

  const location = venue
    ? `${venue.name}${venue.address ? `\n📍 地址: ${venue.address}` : ''}`
    : `${event.venueName || '待定'}`;

  const maxParticipants = event.maxParticipants || 0;
  const spotsLeft = Math.max(0, maxParticipants - registrationCount);
  const registrationInfo = maxParticipants > 0
    ? (spotsLeft > 0
        ? `已报名 ${registrationCount}/${maxParticipants} · 剩余 ${spotsLeft} 个名额`
        : `已满 (${registrationCount}/${maxParticipants})`)
    : null;

  const priceInfo = event.price > 0 ? `$${event.price} / 人` : '免费';

  const baseMessage = `🏸 ${event.title}

📅 ${dateStr}
⏰ ${fmtTime(eventDate)} - ${fmtTime(eventEndDate)}
📍 ${location}
💰 ${priceInfo}${registrationInfo ? `\n👥 ${registrationInfo}` : ''}

欢迎来打球！🏸✨`;

  if (includeLink) {
    const eventLink = getEventUrl(event.id);
    return `${baseMessage}\n\n🔗 报名链接: ${eventLink}`;
  }

  return baseMessage;
};

/**
 * Copy event details to clipboard in a specific language
 */
export const copyEventToClipboardLang = (event, venue = null, registrationCount = 0, lang = 'en') => {
  const message = lang === 'zh'
    ? generateShareMessageZh(event, venue, registrationCount, true)
    : generateShareMessage(event, venue, registrationCount, true);
  return navigator.clipboard.writeText(message);
};

/**
 * Copy text to clipboard with fallback support
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (fallbackError) {
      console.error('Fallback copy failed:', fallbackError);
      return false;
    }
  }
};